import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import api from '@/lib/api';
import Navbar from '@/components/Navbar';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog';
import { toast } from 'sonner';
import { Radio, Plus, Edit, Trash2, Users, FileText, Loader2, Image, Send } from 'lucide-react';

function AdminChannels() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [channels, setChannels] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [postDialogOpen, setPostDialogOpen] = useState(false);
  const [editingChannel, setEditingChannel] = useState(null);
  const [selectedChannel, setSelectedChannel] = useState(null);
  const [saving, setSaving] = useState(false);

  const [channelForm, setChannelForm] = useState({
    name: '',
    description: '',
    avatar_url: '',
    cover_url: ''
  });

  const [postForm, setPostForm] = useState({
    content: '',
    image_url: ''
  });

  useEffect(() => {
    if (!user || !user.is_admin) {
      navigate('/');
      return;
    }
    fetchChannels();
  }, [user, navigate]);

  async function fetchChannels() {
    try {
      const res = await api.get('/admin/channels');
      setChannels(res.data);
    } catch (err) {
      toast.error('Erro ao carregar canais');
    } finally {
      setLoading(false);
    }
  }

  function openCreateDialog() {
    setEditingChannel(null);
    setChannelForm({ name: '', description: '', avatar_url: '', cover_url: '' });
    setDialogOpen(true);
  }

  function openEditDialog(channel) {
    setEditingChannel(channel);
    setChannelForm({
      name: channel.name,
      description: channel.description || '',
      avatar_url: channel.avatar_url || '',
      cover_url: channel.cover_url || ''
    });
    setDialogOpen(true);
  }

  function openPostDialog(channel) {
    setSelectedChannel(channel);
    setPostForm({ content: '', image_url: '' });
    setPostDialogOpen(true);
  }

  async function handleChannelSubmit(e) {
    e.preventDefault();
    if (!channelForm.name) {
      toast.error('Nome é obrigatório');
      return;
    }

    setSaving(true);
    try {
      if (editingChannel) {
        await api.put('/admin/channels/' + editingChannel.id, channelForm);
        toast.success('Canal atualizado');
      } else {
        await api.post('/admin/channels', channelForm);
        toast.success('Canal criado');
      }
      setDialogOpen(false);
      fetchChannels();
    } catch (err) {
      toast.error('Erro ao guardar canal');
    } finally {
      setSaving(false);
    }
  }

  async function handlePostSubmit(e) {
    e.preventDefault();
    if (!postForm.content) {
      toast.error('Conteúdo é obrigatório');
      return;
    }

    setSaving(true);
    try {
      await api.post('/admin/channels/' + selectedChannel.id + '/posts', {
        channel_id: selectedChannel.id,
        content: postForm.content,
        image_url: postForm.image_url || null
      });
      toast.success('Post publicado!');
      setPostDialogOpen(false);
      fetchChannels();
    } catch (err) {
      toast.error('Erro ao publicar post');
    } finally {
      setSaving(false);
    }
  }

  async function handleDelete(channelId) {
    if (!confirm('Eliminar este canal e todos os posts?')) return;

    try {
      await api.delete('/admin/channels/' + channelId);
      toast.success('Canal eliminado');
      fetchChannels();
    } catch (err) {
      toast.error('Erro ao eliminar');
    }
  }

  async function handleToggleActive(channel) {
    try {
      await api.put('/admin/channels/' + channel.id, { is_active: !channel.is_active });
      toast.success(channel.is_active ? 'Canal desativado' : 'Canal ativado');
      fetchChannels();
    } catch (err) {
      toast.error('Erro ao atualizar');
    }
  }

  async function seedChannels() {
    try {
      await api.post('/admin/channels/seed');
      toast.success('Canais de exemplo criados');
      fetchChannels();
    } catch (err) {
      toast.error('Erro ao criar exemplos');
    }
  }

  if (!user || !user.is_admin) return null;

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-ruby" />
      </div>
    );
  }

  const channelCards = [];
  for (let i = 0; i < channels.length; i++) {
    const channel = channels[i];
    channelCards.push(
      <div 
        key={channel.id}
        className={'bg-surface rounded-xl border p-4 ' + (channel.is_active ? 'border-border' : 'border-red-500/30 opacity-60')}
      >
        <div className="flex gap-4">
          <div className="w-16 h-16 rounded-full overflow-hidden flex-shrink-0">
            <img src={channel.avatar_url || 'https://images.unsplash.com/photo-1518893063132-36e46dbe2428?w=200'} alt="" className="w-full h-full object-cover" />
          </div>
          <div className="flex-1 min-w-0">
            <div className="flex items-start justify-between gap-2">
              <h3 className="font-semibold text-foreground truncate">{channel.name}</h3>
              <span className={'text-xs px-2 py-1 rounded-full ' + (channel.is_active ? 'bg-green-500/20 text-green-500' : 'bg-red-500/20 text-red-500')}>
                {channel.is_active ? 'Ativo' : 'Inativo'}
              </span>
            </div>
            {channel.description && (
              <p className="text-sm text-muted-foreground line-clamp-1">{channel.description}</p>
            )}
            <div className="flex items-center gap-4 mt-2 text-sm text-muted-foreground">
              <span className="flex items-center gap-1">
                <Users className="w-3 h-3" />{channel.follower_count}
              </span>
              <span className="flex items-center gap-1">
                <FileText className="w-3 h-3" />{channel.post_count} posts
              </span>
            </div>
          </div>
        </div>
        <div className="flex flex-wrap gap-2 mt-4">
          <Button variant="outline" size="sm" onClick={function() { openPostDialog(channel); }} className="border-ruby text-ruby hover:bg-ruby/10">
            <Send className="w-4 h-4 mr-1" />Publicar
          </Button>
          <Button variant="outline" size="sm" onClick={function() { openEditDialog(channel); }} className="border-border">
            <Edit className="w-4 h-4 mr-1" />Editar
          </Button>
          <Button variant="outline" size="sm" onClick={function() { handleToggleActive(channel); }} className="border-border">
            {channel.is_active ? 'Desativar' : 'Ativar'}
          </Button>
          <Button variant="outline" size="sm" onClick={function() { handleDelete(channel.id); }} className="border-red-500/30 text-red-400 hover:bg-red-500/10">
            <Trash2 className="w-4 h-4" />
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <Navbar />

      <main className="pt-24 pb-12 px-6 md:px-12">
        <div className="max-w-4xl mx-auto">
          <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-8">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 rounded-xl bg-ruby/10 flex items-center justify-center">
                <Radio className="w-6 h-6 text-ruby" />
              </div>
              <div>
                <h1 className="font-brand text-2xl font-bold text-foreground">Gerir Canais</h1>
                <p className="text-muted-foreground">Cria canais e publique para os leitores</p>
              </div>
            </div>
            <div className="flex gap-2">
              <Button variant="outline" onClick={seedChannels} className="border-border">
                Criar Exemplos
              </Button>
              <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
                <DialogTrigger asChild>
                  <Button onClick={openCreateDialog} className="bg-ruby hover:bg-ruby-light">
                    <Plus className="w-4 h-4 mr-2" />Novo Canal
                  </Button>
                </DialogTrigger>
                <DialogContent className="bg-surface border-border max-w-lg z-50">
                  <DialogHeader>
                    <DialogTitle className="text-foreground">
                      {editingChannel ? 'Editar Canal' : 'Criar Canal'}
                    </DialogTitle>
                  </DialogHeader>
                  <form onSubmit={handleChannelSubmit} className="space-y-4">
                    <div className="space-y-2">
                      <Label>Nome *</Label>
                      <Input
                        value={channelForm.name}
                        onChange={function(e) { setChannelForm({ ...channelForm, name: e.target.value }); }}
                        placeholder="Nome do canal"
                        className="bg-surface-light border-border"
                        required
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>Descrição</Label>
                      <Textarea
                        value={channelForm.description}
                        onChange={function(e) { setChannelForm({ ...channelForm, description: e.target.value }); }}
                        placeholder="Descrição breve"
                        className="bg-surface-light border-border"
                        rows={2}
                      />
                    </div>
                    <div className="space-y-2">
                      <div className="flex items-center justify-between flex-wrap gap-2">
                        <Label>URL do Avatar</Label>
                        <span className="inline-flex items-center gap-1.5 rounded-full bg-ruby/10 border border-ruby/30 px-2.5 py-0.5 text-[11px] font-medium text-ruby">
                          400×400 · 1:1 · até 100 KB
                        </span>
                      </div>
                      <Input
                        value={channelForm.avatar_url}
                        onChange={function(e) { setChannelForm({ ...channelForm, avatar_url: e.target.value }); }}
                        placeholder="https://..."
                        className="bg-surface-light border-border"
                      />
                      <p className="text-xs text-muted-foreground">Formato quadrado — aparece como círculo na UI.</p>
                    </div>
                    <div className="space-y-2">
                      <div className="flex items-center justify-between flex-wrap gap-2">
                        <Label>URL da Capa</Label>
                        <span className="inline-flex items-center gap-1.5 rounded-full bg-ruby/10 border border-ruby/30 px-2.5 py-0.5 text-[11px] font-medium text-ruby">
                          1600×400 · 4:1 · até 250 KB
                        </span>
                      </div>
                      <Input
                        value={channelForm.cover_url}
                        onChange={function(e) { setChannelForm({ ...channelForm, cover_url: e.target.value }); }}
                        placeholder="https://..."
                        className="bg-surface-light border-border"
                      />
                      <p className="text-xs text-muted-foreground">Banner panorâmico no topo do canal.</p>
                    </div>
                    <div className="flex gap-2 pt-2">
                      <Button type="button" variant="outline" onClick={function() { setDialogOpen(false); }} className="flex-1 border-border">
                        Cancelar
                      </Button>
                      <Button type="submit" disabled={saving} data-testid="submit-channel" className="flex-1 bg-ruby hover:bg-ruby-light relative z-50">
                        {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : (editingChannel ? 'Atualizar' : 'Criar')}
                      </Button>
                    </div>
                  </form>
                </DialogContent>
              </Dialog>
            </div>
          </div>

          {/* Post Dialog */}
          <Dialog open={postDialogOpen} onOpenChange={setPostDialogOpen}>
            <DialogContent className="bg-surface border-border max-w-lg z-50">
              <DialogHeader>
                <DialogTitle className="text-foreground">
                  Publicar em {selectedChannel?.name}
                </DialogTitle>
              </DialogHeader>
              <form onSubmit={handlePostSubmit} className="space-y-4">
                <div className="space-y-2">
                  <Label>Conteúdo *</Label>
                  <Textarea
                    value={postForm.content}
                    onChange={function(e) { setPostForm({ ...postForm, content: e.target.value }); }}
                    placeholder="Escreve sua mensagem para os seguidores..."
                    className="bg-surface-light border-border min-h-[150px]"
                    required
                  />
                </div>
                <div className="space-y-2">
                  <Label>URL da Imagem (opcional)</Label>
                  <Input
                    value={postForm.image_url}
                    onChange={function(e) { setPostForm({ ...postForm, image_url: e.target.value }); }}
                    placeholder="https://..."
                    className="bg-surface-light border-border"
                  />
                  {postForm.image_url && (
                    <img src={postForm.image_url} alt="Preview" className="w-full h-32 object-cover rounded mt-2" />
                  )}
                </div>
                <div className="flex gap-2 pt-2">
                  <Button type="button" variant="outline" onClick={function() { setPostDialogOpen(false); }} className="flex-1 border-border">
                    Cancelar
                  </Button>
                  <Button type="submit" disabled={saving} className="flex-1 bg-ruby hover:bg-ruby-light">
                    {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Publicar'}
                  </Button>
                </div>
              </form>
            </DialogContent>
          </Dialog>

          {/* Stats */}
          <div className="grid grid-cols-3 gap-4 mb-8">
            <div className="bg-surface rounded-xl border border-border p-4">
              <p className="text-muted-foreground text-sm">Total de Canais</p>
              <p className="text-2xl font-bold text-foreground">{channels.length}</p>
            </div>
            <div className="bg-surface rounded-xl border border-border p-4">
              <p className="text-muted-foreground text-sm">Ativos</p>
              <p className="text-2xl font-bold text-green-500">
                {channels.filter(function(c) { return c.is_active; }).length}
              </p>
            </div>
            <div className="bg-surface rounded-xl border border-border p-4">
              <p className="text-muted-foreground text-sm">Total de Posts</p>
              <p className="text-2xl font-bold text-foreground">
                {channels.reduce(function(sum, c) { return sum + c.post_count; }, 0)}
              </p>
            </div>
          </div>

          {/* Channels List */}
          {channels.length > 0 ? (
            <div className="space-y-4">{channelCards}</div>
          ) : (
            <div className="text-center py-20">
              <Radio className="w-16 h-16 text-muted mx-auto mb-4" />
              <h2 className="text-xl font-semibold text-foreground mb-2">Nenhum canal criado</h2>
              <p className="text-muted-foreground mb-6">Cria seu primeiro canal ou usa os exemplos</p>
              <div className="flex justify-center gap-4">
                <Button variant="outline" onClick={seedChannels} className="border-border">
                  Criar Exemplos
                </Button>
                <Button onClick={openCreateDialog} className="bg-ruby hover:bg-ruby-light">
                  <Plus className="w-4 h-4 mr-2" />Criar Canal
                </Button>
              </div>
            </div>
          )}
        </div>
      </main>
    </div>
  );
}

export default AdminChannels;
